import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useLocation, useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import { Row, Col, Form, FormGroup, Input, Card, CardBody } from "reactstrap";

import { DefaultButton } from "../../../AbstractElements";
import {
  ALL_COURSE_FILTER_STATUS,
  EXPERTISE_LEVEL_STATUS,
} from "../../../Api/constants";
import FixedFooter from "../../../CommonElements/FixedFooter";
import LabelTooltip from "../../../CommonElements/LabelTooltip";
import SearchDropDownPaginator from "../../../CommonElements/SearchDropDownPaginator";
import { useAppDispatch, useAppSelector } from "../../../ReduxToolkit/Hooks";
import {
  createTrainerActivity,
  updateTrainerActivity,
} from "../../../ReduxToolkit/Reducers/ActivityTrackingSlice";
import { getCourseList } from "../../../ReduxToolkit/Reducers/CourseSlice";
import { getTrainingAidList } from "../../../ReduxToolkit/Reducers/MasterSlice";
import {
  ActivityCategory,
  Category,
  CourseName,
  Description,
  Duration,
  DurationInMinutes,
  DurationLimitValidationMessage,
  Enter,
  ActivityOn,
  LeaveType,
  PleaseEnter,
  PleaseSelect,
  PleaseSelectActivityCategory,
  Select,
  Submit,
  TrainingAidName,
  DescriptionLimitationMessage,
} from "../../../utils/Constant";
import {
  activityCategoryOptions,
  customStyles,
  formatDate,
  handleKeyDownOnNumericWithoutDecimal,
  leaveTypes,
} from "../../../utils/helper/helper";

interface FormType {
  category?: string;
  description?: string;
  date?: Date;
  duration?: string;
  leaveType?: string;
  trainingAidName?: string;
  courseName?: string;
}

interface FormErrors {
  category?: string;
  description?: string;
  date?: string;
  duration?: string;
  trainingAidName?: string;
  courseName?: string;
  leaveType?: string;
}

const UpsertActivityTracking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const editData = location.state?.row;
  const { trainingList } = useAppSelector(state => state.master);
  const { courseList } = useAppSelector(state => state.course);
  const [courseListData, setCourseListData] = useState([]);
  const [trainingAidsData, setTrainingAidsData] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedTrainingAidId, setSelectedTrainingAidId] = useState(null);

  const [category, setCategory] = useState(
    editData?.category || "self_learning"
  );
  const [form, setForm] = useState<Partial<FormType>>({
    ...editData,
    date: editData?.date ? new Date(editData.date) : new Date(),
  });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    dispatch(getTrainingAidList());
    dispatch(
      getCourseList({
        status: EXPERTISE_LEVEL_STATUS,
        course_type: ALL_COURSE_FILTER_STATUS,
        domain: null,
      })
    );
  }, []);

  useEffect(() => {
    const generatedData = [];
    generatedData.push({ label: "Select", value: "" });
    if (courseList) {
      courseList?.course_list?.forEach(item => {
        generatedData.push({ label: item?.course_name, value: item?.id });
      });
    }
    if (editData?.courseId) setSelectedCourseId(editData?.courseId);

    setCourseListData(generatedData);
  }, [courseList]);

  useEffect(() => {
    const generatedData = [];
    generatedData.push({ label: "Select", value: "" });
    if (courseList) {
      trainingList?.resources?.forEach(item => {
        generatedData.push({ label: item?.name, value: item?.id });
      });
    }
    if (editData?.trainingAidId)
      setSelectedTrainingAidId(editData?.trainingAidId);

    setTrainingAidsData(generatedData);
  }, [trainingList]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: undefined,
    }));

    setForm(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (field, selectedDate) => {
    if (!selectedDate) return;

    setErrors(prevErrors => ({
      ...prevErrors,
      [field]: undefined,
    }));

    setForm(prevState => ({
      ...prevState,
      [field]: selectedDate,
    }));
  };

  const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCategory(event.target.value);
    setForm({});
    setErrors({});
    setSelectedCourseId(null);
    setSelectedTrainingAidId(null);
  };

  const renderFields = () => {
    switch (category) {
      case "self_learning":
      case "documentation_work":
      case "travel":
      case "others":
        return (
          <Row>
            <Col md="6">
              <FormGroup>
                <LabelTooltip
                  label={ActivityOn}
                  tooltipText={ActivityOn}
                  important={false}
                  placement={"bottom"}
                />
                <div>
                  <DatePicker
                    selected={form?.date}
                    onChange={(date: Date | null) =>
                      handleDateChange("date", date)
                    }
                    placeholderText={`${Select} ${ActivityOn}`}
                    className="form-control"
                    dateFormat="dd-MM-yyyy"
                    disabledKeyboardNavigation={true}
                    onKeyDown={e => e.preventDefault()}
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
                {errors.date && (
                  <div className="mt-1 text-danger">{errors.date}</div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <LabelTooltip
                  label={Duration}
                  important
                  tooltipText={DurationInMinutes}
                />
                <Input
                  type="number"
                  name="duration"
                  onChange={handleChange}
                  value={form.duration}
                  onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                  placeholder={`${Enter} ${DurationInMinutes}`}
                  invalid={!!errors.duration}
                  maxLength={3}
                  min={1}
                />
                {errors.duration && (
                  <div className="mt-1 text-danger">{errors.duration}</div>
                )}
              </FormGroup>
            </Col>
          </Row>
        );
      case "training_aid_development":
        return (
          <Row>
            <Col md="6">
              <FormGroup>
                <LabelTooltip
                  label={ActivityOn}
                  tooltipText={ActivityOn}
                  important={false}
                  placement={"bottom"}
                />
                <div>
                  <DatePicker
                    selected={form?.date}
                    onChange={(date: Date | null) =>
                      handleDateChange("date", date)
                    }
                    placeholderText={`${Select} ${ActivityOn}`}
                    className="form-control"
                    dateFormat="dd-MM-yyyy"
                    disabledKeyboardNavigation={true}
                    onKeyDown={e => e.preventDefault()}
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
                {errors.date && (
                  <div className="mt-1 text-danger">{errors.date}</div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <LabelTooltip
                  label={Duration}
                  important
                  tooltipText={DurationInMinutes}
                />
                <Input
                  type="number"
                  name="duration"
                  onChange={handleChange}
                  value={form.duration}
                  onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                  placeholder={`${Enter} ${DurationInMinutes}`}
                  invalid={!!errors.duration}
                  maxLength={3}
                  min={1}
                />
                {errors.duration && (
                  <div className="mt-1 text-danger">{errors.duration}</div>
                )}
              </FormGroup>
            </Col>
          </Row>
        );
      case "training_aid_maintenance":
        return (
          <>
            <Row>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={TrainingAidName}
                    tooltipText={`${Select} ${TrainingAidName}`}
                    important
                  />
                  <ReactSelect
                    name="trainingAidName"
                    className="text-gray"
                    onChange={selectedOption => {
                      setSelectedTrainingAidId(
                        selectedOption ? selectedOption.value : null
                      );
                    }}
                    value={
                      trainingAidsData?.find(
                        option => option.value === selectedTrainingAidId
                      ) || null
                    }
                    options={trainingAidsData?.map(option => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    isSearchable
                    isClearable={false}
                    styles={customStyles}
                    components={{
                      MenuList: props => (
                        <SearchDropDownPaginator {...props} maxHeight={250} />
                      ),
                    }}
                    filterOption={(option, inputValue) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(inputValue?.toLowerCase())
                    }
                  />
                  {errors.trainingAidName && (
                    <div className="mt-1 text-danger">
                      {errors.trainingAidName}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={ActivityOn}
                    tooltipText={ActivityOn}
                    important={false}
                    placement={"bottom"}
                  />
                  <div>
                    <DatePicker
                      selected={form?.date}
                      onChange={(date: Date | null) =>
                        handleDateChange("date", date)
                      }
                      placeholderText={`${Select} ${ActivityOn}`}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      disabledKeyboardNavigation={true}
                      onKeyDown={e => e.preventDefault()}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                  {errors.date && (
                    <div className="mt-1 text-danger">{errors.date}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={Duration}
                    important
                    tooltipText={DurationInMinutes}
                  />
                  <Input
                    type="number"
                    name="duration"
                    onChange={handleChange}
                    value={form.duration}
                    onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                    placeholder={`${Enter} ${DurationInMinutes}`}
                    invalid={!!errors.duration}
                    maxLength={3}
                    min={1}
                  />
                  {errors.duration && (
                    <div className="mt-1 text-danger">{errors.duration}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </>
        );
      case "training_material_preparation":
        return (
          <>
            <Row>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={CourseName}
                    tooltipText={`${Select} ${CourseName}`}
                    important
                  />
                  <ReactSelect
                    name="course"
                    className="text-gray"
                    onChange={selectedOption => {
                      setSelectedCourseId(
                        selectedOption ? selectedOption.value : null
                      );
                    }}
                    value={
                      courseListData?.find(
                        option => option.value === selectedCourseId
                      ) || null
                    }
                    options={courseListData?.map(option => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    isSearchable
                    isClearable={false}
                    styles={customStyles}
                    components={{
                      MenuList: props => (
                        <SearchDropDownPaginator {...props} maxHeight={250} />
                      ),
                    }}
                    filterOption={(option, inputValue) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(inputValue?.toLowerCase())
                    }
                  />
                  {errors.courseName && (
                    <div className="mt-1 text-danger">{errors.courseName}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={ActivityOn}
                    tooltipText={ActivityOn}
                    important={false}
                    placement={"bottom"}
                  />
                  <div>
                    <DatePicker
                      selected={form?.date}
                      onChange={(date: Date | null) =>
                        handleDateChange("date", date)
                      }
                      placeholderText={`${Select} ${ActivityOn}`}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      disabledKeyboardNavigation={true}
                      onKeyDown={e => e.preventDefault()}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                  {errors.date && (
                    <div className="mt-1 text-danger">{errors.date}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={Duration}
                    important
                    tooltipText={DurationInMinutes}
                  />
                  <Input
                    type="number"
                    name="duration"
                    onChange={handleChange}
                    value={form.duration}
                    onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                    placeholder={`${Enter} ${DurationInMinutes}`}
                    invalid={!!errors.duration}
                    maxLength={3}
                    min={1}
                  />
                  {errors.duration && (
                    <div className="mt-1 text-danger">{errors.duration}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </>
        );
      case "training_material_update":
        return (
          <>
            <Row>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={CourseName}
                    tooltipText={`${Select} ${CourseName}`}
                    important
                  />
                  <ReactSelect
                    name="course"
                    className="text-gray"
                    onChange={selectedOption => {
                      setSelectedCourseId(
                        selectedOption ? selectedOption.value : null
                      );
                    }}
                    value={
                      courseListData?.find(
                        option => option.value === selectedCourseId
                      ) || null
                    }
                    options={courseListData?.map(option => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    isSearchable
                    isClearable={false}
                    styles={customStyles}
                    components={{
                      MenuList: props => (
                        <SearchDropDownPaginator {...props} maxHeight={250} />
                      ),
                    }}
                    filterOption={(option, inputValue) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(inputValue?.toLowerCase())
                    }
                  />
                  {errors.courseName && (
                    <div className="mt-1 text-danger">{errors.courseName}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={ActivityOn}
                    tooltipText={ActivityOn}
                    important={false}
                    placement={"bottom"}
                  />
                  <div>
                    <DatePicker
                      selected={form?.date}
                      onChange={(date: Date | null) =>
                        handleDateChange("date", date)
                      }
                      placeholderText={`${Select} ${ActivityOn}`}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      disabledKeyboardNavigation={true}
                      onKeyDown={e => e.preventDefault()}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                  {errors.date && (
                    <div className="mt-1 text-danger">{errors.date}</div>
                  )}
                </FormGroup>
              </Col>{" "}
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={Duration}
                    important
                    tooltipText={DurationInMinutes}
                  />
                  <Input
                    type="number"
                    name="duration"
                    onChange={handleChange}
                    value={form.duration}
                    onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                    placeholder={`${Enter} ${DurationInMinutes}`}
                    invalid={!!errors.duration}
                    maxLength={3}
                    min={1}
                  />
                  {errors.duration && (
                    <div className="mt-1 text-danger">{errors.duration}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </>
        );
      case "leave":
        return (
          <>
            <Row>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={LeaveType}
                    tooltipText={`${Select} ${LeaveType}`}
                    important
                  />
                  <Input
                    type="select"
                    name="leaveType"
                    value={form.leaveType || ""}
                    onChange={handleChange}
                    invalid={!!errors.leaveType}
                  >
                    <option value="">{`${Select} ${LeaveType}`}</option>
                    {leaveTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Input>
                  <div className="mt-1 text-danger">{errors.leaveType}</div>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={ActivityOn}
                    tooltipText={ActivityOn}
                    important={false}
                    placement={"bottom"}
                  />
                  <div>
                    <DatePicker
                      selected={form?.date}
                      onChange={(date: Date | null) =>
                        handleDateChange("date", date)
                      }
                      placeholderText={`${Select} ${ActivityOn}`}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      disabledKeyboardNavigation={true}
                      onKeyDown={e => e.preventDefault()}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                  {errors.date && (
                    <div className="mt-1 text-danger">{errors.date}</div>
                  )}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <LabelTooltip
                    label={Duration}
                    important
                    tooltipText={DurationInMinutes}
                  />
                  <Input
                    type="number"
                    name="duration"
                    onChange={handleChange}
                    value={form.duration}
                    onKeyDown={handleKeyDownOnNumericWithoutDecimal}
                    placeholder={`${Enter} ${DurationInMinutes}`}
                    invalid={!!errors.duration}
                    maxLength={3}
                    min={1}
                  />
                  {errors.duration && (
                    <div className="mt-1 text-danger">{errors.duration}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </>
        );
      default:
        return null;
    }
  };

  const handleSubmit = () => {
    const newErrors: FormErrors = {};

    // Validate category
    if (!category) {
      newErrors.category = PleaseSelectActivityCategory;
    }

    // Validate description
    if (!form.description || form.description.trim() === "") {
      newErrors.description = `${PleaseEnter} ${Description}`;
    }
    if (form.description?.length > 255) {
      newErrors.description = DescriptionLimitationMessage;
    }

    // Validate fields based on category
    switch (category) {
      case "self_learning":
      case "documentation_work":
      case "travel":
      case "others":
      case "training_aid_development":
        if (!form.date) {
          newErrors.date = `${PleaseSelect} ${ActivityOn}`;
        }
        if (!form.duration) {
          newErrors.duration = `${PleaseEnter} ${DurationInMinutes}`;
        } else {
          const duration = Number(form.duration);
          if (isNaN(duration) || duration < 1 || duration > 1440) {
            newErrors.duration = DurationLimitValidationMessage;
          }
        }
        break;
      case "training_aid_maintenance":
        if (!selectedTrainingAidId) {
          newErrors.trainingAidName = `${PleaseSelect} ${TrainingAidName}`;
        }
        if (!form.date) {
          newErrors.date = `${PleaseSelect} ${ActivityOn}`;
        }
        if (!form.duration) {
          newErrors.duration = `${PleaseEnter} ${DurationInMinutes}`;
        } else {
          const duration = Number(form.duration);
          if (isNaN(duration) || duration < 1 || duration > 1440) {
            newErrors.duration = DurationLimitValidationMessage;
          }
        }
        break;
      case "training_material_preparation":
      case "training_material_update":
        if (!selectedCourseId) {
          newErrors.courseName = `${PleaseSelect} ${CourseName}`;
        }
        if (!form.date) {
          newErrors.date = `${PleaseSelect} ${ActivityOn}`;
        }
        if (!form.duration) {
          newErrors.duration = `${PleaseEnter} ${DurationInMinutes}`;
        } else {
          const duration = Number(form.duration);
          if (isNaN(duration) || duration < 1 || duration > 1440) {
            newErrors.duration = DurationLimitValidationMessage;
          }
        }
        break;
      case "leave":
        if (!form.leaveType) {
          newErrors.leaveType = `${PleaseSelect} ${LeaveType}`;
        }
        if (!form.date) {
          newErrors.date = `${PleaseSelect} ${ActivityOn}`;
        }
        if (!form.duration) {
          newErrors.duration = `${PleaseEnter} ${DurationInMinutes}`;
        } else {
          const duration = Number(form.duration);
          if (isNaN(duration) || duration < 1 || duration > 1440) {
            newErrors.duration = DurationLimitValidationMessage;
          }
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const expectedPayload: any = {
      activity_category: category,
      description: form.description,
      activity_date: formatDate(form.date),
    };
    if (form.duration) expectedPayload.duration = Number(form.duration);
    if (selectedCourseId) expectedPayload.course_id = selectedCourseId;
    if (selectedTrainingAidId)
      expectedPayload.training_aid_id = selectedTrainingAidId;
    if (form.leaveType) expectedPayload.leave_type = form.leaveType;
    if (editData?.id) expectedPayload.id = editData.id;

    if (editData) {
      dispatch(updateTrainerActivity(expectedPayload)).then(() => {
        navigate(`${process.env.PUBLIC_URL}/log-time`);
      });
    } else {
      dispatch(createTrainerActivity(expectedPayload)).then(() => {
        navigate(`${process.env.PUBLIC_URL}/log-time`);
      });
    }
  };

  return (
    <div className="page-body">
      <div className="container-fluid">
        <Card>
          <CardBody>
            <Form>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <LabelTooltip
                      label={ActivityCategory}
                      tooltipText={`${Select} ${ActivityCategory}`}
                      important
                    />
                    <Input
                      type="select"
                      value={category}
                      onChange={handleCategoryChange}
                      required
                      invalid={!category}
                      disabled={!!location.state}
                    >
                      <option value="">
                        {Select} {Category}
                      </option>
                      {activityCategoryOptions?.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                    {errors.category && (
                      <div className="mt-1 text-danger">{errors.category}</div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              {category && (
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <LabelTooltip
                        label={Description}
                        tooltipText={`${Enter} ${Description}`}
                        important
                      />
                      <Input
                        type="textarea"
                        name="description"
                        rows={4}
                        value={form.description || ""}
                        onChange={handleChange}
                        invalid={!!errors.description}
                        maxLength={255}
                      />
                      {errors.description && (
                        <div className="mt-1 text-danger">
                          {errors.description}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              )}
              {category && renderFields()}
              <div>
                <FixedFooter>
                  <DefaultButton color="primary" onClick={handleSubmit}>
                    {Submit}
                  </DefaultButton>
                </FixedFooter>
              </div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default UpsertActivityTracking;
